import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import useAuthStore from '../../stores/authStore';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import './Login.css';

export default function Login() {
    const navigate = useNavigate();
    const { login, loading } = useAuthStore();
    const [form, setForm] = useState({ email: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!form.email || !form.password) {
            setError('Please fill in all fields');
            return;
        }

        const result = await login(form.email, form.password);

        if (result.success) {
            navigate('/');
        } else {
            setError(result.error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setError('');
    };

    return (
        <div className="login-container">
            <div className="login-left">
                <div className="login-hero">
                    <div className="hero-pattern" />
                    <div className="hero-content">
                        <h1>E-Procurement<br />Management System</h1>
                        <p>
                            Streamline your procurement process with our comprehensive
                            solution for managing vendors, purchase requests, contracts,
                            and more.
                        </p>
                        <div className="hero-features">
                            <div className="hero-feature">
                                <div className="feature-icon">📋</div>
                                <span>Purchase Request</span>
                            </div>
                            <div className="hero-feature">
                                <div className="feature-icon">🏢</div>
                                <span>Vendor Management</span>
                            </div>
                            <div className="hero-feature">
                                <div className="feature-icon">📝</div>
                                <span>Contract Tracking</span>
                            </div>
                            <div className="hero-feature">
                                <div className="feature-icon">📊</div>
                                <span>Analytics & Reports</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="login-right">
                <div className="login-box">
                    <div className="login-header">
                        <div className="login-logo">
                            <div className="logo-icon">EP</div>
                        </div>
                        <h2>Welcome Back</h2>
                        <p>Sign in to your account</p>
                    </div>

                    <form onSubmit={handleSubmit} className="login-form">
                        {error && (
                            <Alert type="error">{error}</Alert>
                        )}

                        <Input
                            name="email"
                            type="email"
                            label="Email Address"
                            placeholder="Enter your email"
                            value={form.email}
                            onChange={handleChange}
                            icon={Mail}
                            required
                        />

                        <div className="password-field">
                            <Input
                                name="password"
                                type={showPassword ? 'text' : 'password'}
                                label="Password"
                                placeholder="Enter your password"
                                value={form.password}
                                onChange={handleChange}
                                icon={Lock}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>

                        <div className="login-options">
                            <label className="remember-me">
                                <input type="checkbox" />
                                <span>Remember me</span>
                            </label>
                            <Link to="/forgot-password" className="forgot-link">
                                Forgot password?
                            </Link>
                        </div>

                        <Button type="submit" fullWidth loading={loading}>
                            Sign In
                        </Button>
                    </form>

                    <div className="vendor-register-section">
                        <p>Are you a vendor?</p>
                        <Link to="/vendor-registration" className="vendor-register-link">
                            Register as Vendor
                        </Link>
                    </div>

                    <div className="login-footer">
                        <p>© 2024 E-Procurement System. All rights reserved.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
